import { useState, useEffect } from 'react';
import { useLocation, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase';
import { useApp } from '../contexts/AppContext';
import usePageTitle from '../hooks/usePageTitle';

const functions = getFunctions(app);
const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');

function RegisterPage() {
  usePageTitle('Register Your Chapter');

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, loading: authLoading, logout } = useApp();
  
  const prefillData = location.state || {};
  const wasCanceled = searchParams.get('canceled') === 'true';

  const [formData, setFormData] = useState({
    organization: prefillData.organization || '',
    chapter: prefillData.chapter || '',
    school: prefillData.school || '',
    memberCount: prefillData.memberCount || 'standard',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // If user already has an organization, redirect to home
  useEffect(() => {
    if (!authLoading && isAuthenticated && user?.organizationId) {
      navigate('/home', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Show canceled message
  useEffect(() => {
    if (wasCanceled) {
      setApiError('Payment was canceled. Please try again when ready.');
    }
  }, [wasCanceled]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (apiError) {
      setApiError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.organization.trim()) {
      newErrors.organization = 'Organization name is required';
    }
    if (!formData.chapter.trim()) {
      newErrors.chapter = 'Chapter name is required';
    }
    if (!formData.school.trim()) {
      newErrors.school = 'School name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!user?.uid) {
      setApiError('You must be logged in to register an organization.');
      return;
    }

    setLoading(true);
    setApiError('');

    try {
      const result = await createCheckoutSession({
        organization: formData.organization.trim(),
        chapter: formData.chapter.trim(),
        school: formData.school.trim(),
        memberCount: formData.memberCount,
        // Pass the logged-in user's info instead of creating new account
        adminUid: user.uid,
        adminEmail: user.email,
        adminDisplayName: user.displayName || user.email,
        successUrl: `${window.location.origin}/register-success`,
        cancelUrl: `${window.location.origin}/register`,
      });

      if (result.data?.url) {
        window.location.href = result.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setApiError(err.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getPricing = () => {
    return formData.memberCount === 'large' ? 200 : 150;
  };

  const inputClasses = (fieldName) => `
    w-full px-4 py-3 border rounded-lg transition-all duration-200
    ${errors[fieldName] 
      ? 'border-red-400 focus:ring-2 focus:ring-red-200' 
      : 'border-gray-300 focus:ring-2 focus:ring-red-100 focus:border-red-400'}
  `;

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F9FB' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#a60533' }}></div>
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F9FB' }}>
      {/* Header */}
      <nav className="bg-white shadow-sm">
        <div className="container mx-auto px-4">
          {/* The brand keeps one line at 375 wide: it does not shrink, it does
              not wrap, and it drops one size step below the sm breakpoint. The
              signed-in name is what gives way there, because Logout is the
              control the page needs. */}
          <div className="flex justify-between items-center p-4 gap-3">
            <Link to="/" className="flex items-center space-x-3 flex-shrink-0">
              <img
                src="/logo.png"
                alt="One Stop Greek Logo"
                className="h-10 w-10"
              />
              <span
                className="text-xl sm:text-2xl font-bold whitespace-nowrap"
                style={{ color: '#a60533' }}
              >
                One Stop Greek
              </span>
            </Link>
            <div className="flex items-center space-x-4 min-w-0">
              <span className="hidden sm:inline text-sm text-gray-600 truncate">
                {user?.displayName || user?.email}
              </span>
              <button
                onClick={handleLogout}
                className="py-2 px-4 rounded-md text-sm border transition flex-shrink-0 whitespace-nowrap"
                style={{ color: '#a60533', borderColor: '#a60533' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#fce7ed'}
                onMouseLeave={(e) => e.target.style.backgroundColor = ''}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Form Section */}
            <div className="lg:col-span-3 bg-white rounded-xl shadow-lg p-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Register Your Chapter
              </h1>
              <p className="text-gray-600 mb-8">
                Complete the form below to register your organization with One Stop Greek.
              </p>

              {apiError && (
                <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-red-700 text-sm">{apiError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Admin Info Section */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-gray-800 pb-2 border-b border-gray-200">
                    Primary Administrator
                  </h2>
                  
                  <div className="p-4 rounded-lg" style={{ backgroundColor: '#fce7ed' }}>
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: '#a60533' }}>
                        {(user?.displayName || user?.email || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {user?.displayName || 'User'}
                        </p>
                        <p className="text-sm text-gray-600">{user?.email}</p>
                      </div>
                    </div>
                    <p className="mt-3 text-sm" style={{ color: '#8a0429' }}>
                      You will be the primary administrator for this organization. You can add additional admins after registration.
                    </p>
                  </div>
                </div>

                {/* Organization Details Section */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-gray-800 pb-2 border-b border-gray-200">
                    Organization Details
                  </h2>

                  <div>
                    <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-1">
                      Greek Organization *
                    </label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      placeholder="e.g., Theta Chi"
                      className={inputClasses('organization')}
                    />
                    {errors.organization && (
                      <p className="mt-1 text-sm text-red-500">{errors.organization}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="chapter" className="block text-sm font-medium text-gray-700 mb-1">
                      Greek Chapter *
                    </label>
                    <input
                      type="text"
                      id="chapter"
                      name="chapter"
                      value={formData.chapter}
                      onChange={handleInputChange}
                      placeholder="e.g., Alpha Omicron"
                      className={inputClasses('chapter')}
                    />
                    {errors.chapter && (
                      <p className="mt-1 text-sm text-red-500">{errors.chapter}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="school" className="block text-sm font-medium text-gray-700 mb-1">
                      School Name *
                    </label>
                    <input
                      type="text"
                      id="school"
                      name="school"
                      value={formData.school}
                      onChange={handleInputChange}
                      placeholder="e.g., University of North Texas"
                      className={inputClasses('school')}
                    />
                    {errors.school && (
                      <p className="mt-1 text-sm text-red-500">{errors.school}</p>
                    )}
                  </div>
                </div>

                {/* Chapter Size Section */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-gray-800 pb-2 border-b border-gray-200">
                    Chapter Size
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label
                      className={`relative flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        formData.memberCount === 'standard'
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="memberCount"
                        value="standard"
                        checked={formData.memberCount === 'standard'}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <span className="text-lg font-semibold text-gray-900">Standard</span>
                      <span className="text-sm text-gray-500">Up to 100 members</span>
                      <span className="mt-2 text-2xl font-bold" style={{ color: '#a60533' }}>
                        $150<span className="text-sm font-normal text-gray-500">/semester</span>
                      </span>
                      {formData.memberCount === 'standard' && (
                        <span className="absolute top-2 right-2">
                          <svg className="w-6 h-6" style={{ color: '#a60533' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </span>
                      )}
                    </label>

                    <label
                      className={`relative flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        formData.memberCount === 'large'
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="memberCount"
                        value="large"
                        checked={formData.memberCount === 'large'}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <span className="text-lg font-semibold text-gray-900">Large Chapter</span>
                      <span className="text-sm text-gray-500">100+ members</span>
                      <span className="mt-2 text-2xl font-bold" style={{ color: '#a60533' }}>
                        $200<span className="text-sm font-normal text-gray-500">/semester</span>
                      </span>
                      {formData.memberCount === 'large' && (
                        <span className="absolute top-2 right-2">
                          <svg className="w-6 h-6" style={{ color: '#a60533' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </span>
                      )}
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 px-6 text-white rounded-lg font-semibold text-base sm:text-lg whitespace-nowrap transition-all duration-200 flex items-center justify-center"
                  style={{ 
                    backgroundColor: loading ? '#ccc' : '#a60533',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#8a0429')}
                  onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#a60533')}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      Continue to Payment - ${getPricing()}
                    </>
                  )}
                </button>

                <p className="text-center text-sm text-gray-500">
                  You'll be redirected to Stripe to complete your payment securely.
                </p>
              </form>
            </div>

            {/* Sidebar - Order Summary */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
                
                <div className="space-y-3 pb-4 border-b border-gray-200">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Plan</span>
                    <span className="font-medium text-gray-900">
                      {formData.memberCount === 'large' ? 'Large Chapter' : 'Standard Chapter'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Members</span>
                    <span className="font-medium text-gray-900">
                      {formData.memberCount === 'large' ? '100+' : 'Up to 100'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Billing</span>
                    <span className="font-medium text-gray-900">Per Semester</span>
                  </div>
                </div>

                <div className="py-4 border-b border-gray-200">
                  <div className="flex justify-between items-baseline">
                    <span className="text-gray-900 font-semibold">Total Today</span>
                    <span className="text-2xl font-bold" style={{ color: '#a60533' }}>
                      ${getPricing()}
                    </span>
                  </div>
                </div>

                <div className="pt-4 space-y-3">
                  <h4 className="font-medium text-gray-900">What's included:</h4>
                  <ul className="space-y-2">
                    {[
                      'Unlimited study point tracking',
                      'Member progress dashboards',
                      'Admin management tools',
                      'In-app messaging',
                      'Custom point categories',
                      'Weekly reports'
                    ].map((feature, index) => (
                      <li key={index} className="flex items-start text-sm text-gray-600">
                        <svg className="w-5 h-5 mr-2 flex-shrink-0" style={{ color: '#a60533' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: '#fce7ed' }}>
                  <p className="text-sm" style={{ color: '#8a0429' }}>
                    <strong>Billing Schedule:</strong> Fall semester billed August 15th, Spring semester billed January 1st.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500 mb-4">Secure payment powered by</p>
            <div className="flex items-center justify-center space-x-2">
              <svg className="h-8" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M59.64 14.28c0-4.76-2.3-8.52-6.7-8.52-4.42 0-7.1 3.76-7.1 8.48 0 5.6 3.16 8.44 7.7 8.44 2.22 0 3.9-.5 5.16-1.2v-3.7c-1.26.64-2.7 1.02-4.52 1.02-1.8 0-3.38-.64-3.58-2.82h9.02c0-.24.02-1.2.02-1.7zm-9.12-1.76c0-2.1 1.28-2.98 2.46-2.98 1.14 0 2.34.88 2.34 2.98h-4.8zM38.34 5.76c-1.8 0-2.96.84-3.6 1.42l-.24-1.14h-4.06v21.62l4.62-.98.02-5.24c.66.48 1.62 1.16 3.22 1.16 3.26 0 6.22-2.62 6.22-8.38-.02-5.28-3.02-8.46-6.18-8.46zm-1.08 13.02c-1.08 0-1.72-.38-2.16-.86l-.02-6.8c.48-.52 1.14-.9 2.18-.9 1.66 0 2.8 1.86 2.8 4.28 0 2.46-1.12 4.28-2.8 4.28zM25.94 4.6l4.64-.98V0l-4.64.98v3.62zM25.94 6.04h4.64v16.46h-4.64V6.04zM21.12 7.38l-.3-1.34h-3.98v16.46h4.62V11.3c1.1-1.42 2.94-1.16 3.52-.96V6.04c-.6-.22-2.78-.64-3.86 1.34zM12.58 2.12l-4.5.96-.02 15.08c0 2.78 2.1 4.84 4.88 4.84 1.54 0 2.68-.28 3.3-.62v-3.74c-.6.24-3.58 1.1-3.58-1.66V9.88h3.58V6.04h-3.58l-.08-3.92zM4.22 10.46c0-.72.6-1 1.58-1 1.42 0 3.2.44 4.62 1.2V6.46c-1.54-.62-3.08-.86-4.62-.86C2.26 5.6 0 7.48 0 10.66c0 4.94 6.8 4.14 6.8 6.28 0 .86-.74 1.14-1.78 1.14-1.54 0-3.5-.64-5.06-1.5v4.26c1.72.74 3.46 1.06 5.06 1.06 3.6 0 6.08-1.78 6.08-5.02-.02-5.34-6.88-4.38-6.88-6.42z" fill="#6772E5"/>
              </svg>
              <span className="text-gray-400">|</span>
              <span className="text-sm text-gray-500">256-bit SSL encryption</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
